/*
    Cart controller mixin
    - All cart related functionality runs through this mixin
    - XXX → app.js
            ↳ cartController.js 
                ↳ cartLensPricing.js
                ↳ cartLineItems.js
                ↳ cartMinicart.js
*/
import Vue from 'vue';
import axios from 'axios';
import { xhrRequestConfig } from '../utils/api';

import cartLensPricing from './cartLensPricing.js';
import cartLineItems from './cartLineItems.js';
import cartMiniCart from './cartMiniCart.js';

const OPTION_PRODUCT_KEY = '_optionProduct';

export default function cart() {
    return {
        mixins: [
            cartMiniCart(),
            cartLineItems(),
            cartLensPricing()
        ],

        data: {
            isCartLoading: false,
            cart: null,
            cartProducts: {
                'baseProducts': [],
                'baseProductsCount': 0,
                'optionProducts': [],
                'optionProductsCount': 0,
            },
        },

        methods: {
            /////////////////////////////////////////////
            // Cart methods

            async addProduct(item) {
                let itemProps = Array.isArray(item.properties) ? Object.fromEntries(item.properties) : item.properties,
                    productTitle = `${item.vendor} - ${itemProps._modelName}`,
                    requestData = {
                        'items': [
                            { // this is the base product, update the values 
                                ...item,
                                properties: {
                                    ...itemProps,
                                    '_uniqueID': `${Math.floor(Math.random() * Math.floor(Math.random() * Date.now()))}`, // Adding a unique ID to force Shopify to list this as a separate line item
                                }
                            }
                        ]
                    };

                if (!!itemProps._prescriptionOptionGroup) {
                    // build out a new request object of the base product and all option products
                    let newPrescriptionOptionGroup = `${item.id}-options-${Math.floor(Math.random()* Date.now())}`,
                        currentOptionGroupKey = itemProps._prescriptionOptionGroup,
                        requestDataAppend = [
                            ...this.cart.items.filter( cartItem => { // these are the option products
                                let cartItemProps = Array.isArray(cartItem.properties) ? Object.fromEntries(cartItem.properties) : cartItem.properties;
                                return cartItemProps._prescriptionOptionGroup === currentOptionGroupKey
                            })
                            .filter(cartItem => cartItem.key !== item.key) // If it is the base product, remove it
                            .map(cartItem => { // then update the properties values
                                let cartItemProps = Array.isArray(cartItem.properties) ? Object.fromEntries(cartItem.properties) : cartItem.properties;
                                return { // this is the base product
                                    ...cartItem,
                                    properties: {
                                        ...cartItemProps,
                                        '_prescriptionOptionGroup': newPrescriptionOptionGroup,
                                        '_uniqueID': `${Math.floor(Math.random() * Math.floor(Math.random() * Date.now()))}`, // Adding a unique ID to force Shopify to list this as a separate line item
                                    }
                                }
                            })
                        ];

                        requestData.items[0].properties._prescriptionOptionGroup = newPrescriptionOptionGroup;
                        requestData.items = [
                            ...requestData.items,
                            ...requestDataAppend
                        ];
                };

                await axios.post('/cart/add.js', requestData, xhrRequestConfig).then(async (response) => {
                    await this.getCart(true);
                    // this.isMinicartOpen = true;
                    this.cartMessage = {
                        show: true,
                        message: `You added ${productTitle} to your shopping cart`,
                        type: "success"
                    };
                }).catch((error) => {
                    console.log(error);

                    this.cartMessage = {
                        show: true,
                        message: error.response.data.description,
                        type: "error"
                    };
                })
            },
            removeProduct(item, quantity, line = false) {
                let itemProps = Array.isArray(item.properties) ? Object.fromEntries(item.properties) : item.properties
                // if removing a product with children/options, need to do it a bit differently,
                // otherwise just fall back to the normal way
                if (!!itemProps._prescriptionOptionGroup) {
                    
                    let optionGroupKey = itemProps._prescriptionOptionGroup,
                        updateArray = {
                            'updates': this.cart.items.map( cartItem => {
                                let cartItemProps = Array.isArray(cartItem.properties) ? Object.fromEntries(cartItem.properties) : cartItem.properties;
                                if (cartItemProps._prescriptionOptionGroup === optionGroupKey) return 0
                                return cartItem.quantity
                            })
                        };

                        axios.post('/cart/update.js', updateArray).then(async (response) => {
                            await this.getCart(true);         
                        }).catch((error) => {
                            console.log(error);
                        });

                } else {
                    this.updateQuantity(item, quantity, line);
                };
            },
            updateQuantity(item, quantity, line = false) {
                this.isCartLoading = true;
                // item.isLoading = true;
                Vue.set(this.cart.items, this.cart.items.indexOf(item), item);

                let data = {
                    "quantity": quantity != undefined ? quantity : item.quantity // Can't use !, quantity could be 0
                };
                
                // If passed through the LINE prop, use that as the reference...
                if (!!line) {
                    data["id"] = item.key;
                } 
                // ... otherwise use the line item key
                else {
                    data["id"] = item.key;
                }

                axios.post('/cart/change.js', data).then(async (response) => {
                    await this.getCart(true);

                    this.isCartLoading = false;
                }).catch(async ({ response, request, message }) => {
                    if (response) {
                        // Fetch cart to set the item's latest available maximum quantity
                        // note: product order may reshuffle so we have to find the item again via key property
                        const oldItemKey = item.key
              
                        await this.getCart(true);
              
                        const newItem = this.cart.items.find(item => item.key === oldItemKey)
                        const newItemIndex = this.cart.items.indexOf(newItem)
                        newItem.message = response.data.message
                        Vue.set(this.cart.items, newItemIndex, newItem)
                    } else if (request) {
                        // The request was made but no response was received
                        // `error.request` is an instance of XMLHttpRequest
                        console.log('request error', request)
                    } else {
                        // Something happened in setting up the request that triggered an Error
                        console.log('unknown error', message)
                    }

                    this.isCartLoading = false;
                });
            },

            addToCart(variantId, quantity=1, properties) {
                this.isCartLoading = true;
                if (!variantId) return;

                let data = {
                    quantity: quantity,
                    id: variantId
                }

                if (properties) {
                    data.properties = properties;
                }

                return axios.post('/cart/add.js', data, xhrRequestConfig).then(async (response) => {
                    await this.getCart(true);
                    this.isMinicartOpen = true;

                }).catch((error) => {
                    console.log(error.response);
                    // Catch inventory errors
                    if (error.response.status == 422) {
                        alert(error.response.data.description);
                    } else {
                        alert('Cannot reach the server at this time, please try again later.');
                        throw error;
                    }
                })
            },
            
            // Putting this in a method to make the timing more accurate,
            // originally had it in a watch but the delay was causing problems
            _updateCartSubTotals() {
                if (!!this.cart && !!this.cart.items) {

                    // need to make the logic a bit more complex, because the properties can either be an object or array,
                    // and each type requires a different way to check for the value we want.
                    let baseProducts = this.cart.items.filter( item => !item.properties.flat(1).includes(OPTION_PRODUCT_KEY)),
                        optionProducts = this.cart.items.filter( item => !!item.properties.flat(1).includes(OPTION_PRODUCT_KEY));

                    // find the option products of a baseProduct, and generate the _shownPrice tag and add in any discount allocation titles to the parent
                    if (!!optionProducts) {
                        baseProducts.forEach( baseProduct => {
                            let props = Array.isArray(baseProduct.properties) ? Object.fromEntries(baseProduct.properties) : baseProduct.properties,
                                optionGroupKey = props._prescriptionOptionGroup,
                                options = optionProducts.filter( optionProduct => {
                                    let optionProps = Array.isArray(optionProduct.properties) ? Object.fromEntries(optionProduct.properties) : optionProduct.properties
                                    return optionProps._prescriptionOptionGroup === optionGroupKey
                                }),
                                shownPriceTotal = baseProduct.final_price + (options.reduce( (acc, option) => acc += option.final_price, 0)),
                                baseProductDiscountMessage = props._discountMessage,
                                optionProductsDiscountTitles = false,
                                baseProductDiscountTitles = false;

                                if (!baseProductDiscountMessage) {

                                    baseProductDiscountTitles = baseProduct.discount_allocation.map(discount => {
                                        let title = discount.title;
                                        if (title.charAt(0) === '_') return false;
                                        return title
                                    }).filter( title => !!title );

                                    optionProductsDiscountTitles = options.map( option => {
                                            if (!option.discount_allocation.length) return false
                                            return option.discount_allocation.map(discount => {
                                                let title = discount.title;
                                                if (title.charAt(0) === '_') return false;
                                                return title
                                            }).filter( title => !!title )
                                        })
                                        .filter( title => !!title )
                                        .flat();
                                    
                                    if (!!baseProductDiscountTitles.length || !!optionProductsDiscountTitles.length) {
                                        baseProductDiscountMessage = baseProductDiscountTitles.concat(optionProductsDiscountTitles).join(', ');

                                       if (props._discountMessage) {
                                        props._discountMessage = baseProductDiscountMessage;
                                       }
                                    }
                                    
                                }
                            
                            if (props._shownPrice) {
                                props._shownPrice = shownPriceTotal;
                            }

                            baseProduct.properties = Object.entries(props);
                        });
                    };
                    
                    this.cartProducts.baseProducts = baseProducts;
                    this.cartProducts.baseProductsCount = baseProducts.length;
                    this.cartProducts.optionProducts = optionProducts;
                    this.cartProducts.optionProductsCount = optionProducts.length;
                }
            },

            async getCart(runLensPrice = false) {
                this.isCartLoading = true;

                await axios.get('/cart?view=data').then( ({ data }) => {
                    this.cart = data;
                    this._updateCartSubTotals()
                }).catch((error) => {
                    console.log(error);
                }).then( () => {
                    if (!!this.cart.item_count && runLensPrice) {
                        this.lensPricingEntry();
                    }
                    // If lens price is false then we end the process here
                    if (!runLensPrice) {
                        this.isCartLoading = false;
                    }
                })
            },
        }
    }
}